import AddRoundedIcon from '@mui/icons-material/AddRounded';
import DeleteOutlineRoundedIcon from '@mui/icons-material/DeleteOutlineRounded';
import EditOutlinedIcon from '@mui/icons-material/EditOutlined';
import { Box, Button, Card, CardActions, CardContent, Chip, Divider, IconButton, Typography } from '@mui/material';
import React from 'react';

import { ProgramWorkoutItemRecord } from '../../api/programs.interface';
import { createVisibleProgramTagDisplayLabels, getProgramWorkoutItemValidationMessages } from './programs.utils';

interface Props {
    day: number;
    tagNamesByTag?: Record<number, string>;
    item?: ProgramWorkoutItemRecord;
    onAddDay?: (day: number) => void;
    onDeleteDay?: (day: number) => void;
    onEditDay?: (day: number) => void;
    readOnly?: boolean;
    variant?: 'builder' | 'detail';
}

const ProgramDayCard: React.FC<Props> = ({ day, item, onAddDay, onDeleteDay, onEditDay, readOnly, tagNamesByTag = {}, variant = 'detail' }) => {
    const primaryLabels = createVisibleProgramTagDisplayLabels(item?.primaryTag, tagNamesByTag);
    const secondaryLabels = createVisibleProgramTagDisplayLabels(item?.secondaryTag, tagNamesByTag);
    const showThumbnail = Boolean(item?.imageUrl);
    const validationMessages = getProgramWorkoutItemValidationMessages(item);

    return (
        <Card sx={{ borderRadius: 3, minHeight: variant === 'builder' ? 208 : 232, overflow: 'hidden' }} variant="outlined">
            <CardContent sx={{ p: 0 }}>
                {!item && (
                    <Box sx={{ display: 'grid', gap: 1.5, p: 2.25 }}>
                        <Typography sx={{ fontWeight: 700 }} variant="subtitle1">Day {day}</Typography>
                        <Typography color="text.secondary" variant="body2">No workout configured</Typography>
                    </Box>
                )}
                {item && (
                    <Box sx={{ display: 'grid', gridTemplateColumns: { md: showThumbnail ? '110px minmax(0, 1fr)' : 'minmax(0, 1fr)', xs: 'minmax(0, 1fr)' } }}>
                        {showThumbnail && (
                            <Box
                                sx={{
                                    backgroundImage: `url(${item.imageUrl})`,
                                    backgroundPosition: 'center',
                                    backgroundSize: 'cover',
                                    display: { md: 'block', xs: 'none' },
                                    minHeight: '100%',
                                }}
                            />
                        )}
                        <Box sx={{ display: 'grid', gap: 1.25, p: 2.25 }}>
                            <Box sx={{ alignItems: 'flex-start', display: 'flex', justifyContent: 'space-between', gap: 1.5 }}>
                                <Box>
                                    <Typography sx={{ fontWeight: 700 }} variant="subtitle1">Day {day}</Typography>
                                    <Typography color="text.secondary" sx={{ mt: 0.35 }} variant="body2">{item.description}</Typography>
                                </Box>
                                <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.75, justifyContent: 'flex-end' }}>
                                    {item.level && <Chip label={item.level} size="small" />}
                                    {validationMessages.length > 0 && <Chip color="warning" label={`Needs review (${validationMessages.length})`} size="small" variant="outlined" />}
                                </Box>
                            </Box>
                            <Box sx={{ alignItems: 'center', display: 'flex', flexWrap: 'wrap', gap: 1.25 }}>
                                {item.time && <Typography variant="body2"><strong>Time:</strong> {item.time}</Typography>}
                                {item.rounds !== undefined && <Typography variant="body2"><strong>Rounds:</strong> {item.rounds}</Typography>}
                            </Box>
                            <Divider />
                            <Box sx={{ display: 'grid', gap: 1 }}>
                                <Typography variant="body2"><strong>Primary:</strong> {item.primaryType}</Typography>
                                {primaryLabels.labels.length > 0 && (
                                    <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.75 }}>
                                        {primaryLabels.labels.map((label) => (
                                            <Chip key={label} label={label} size="small" variant="outlined" />
                                        ))}
                                        {primaryLabels.hiddenCount > 0 && <Chip label={`+${primaryLabels.hiddenCount}`} size="small" variant="outlined" />}
                                    </Box>
                                )}
                            </Box>
                            {item.secondaryType && (
                                <Box sx={{ display: 'grid', gap: 1 }}>
                                    <Typography variant="body2"><strong>Secondary:</strong> {item.secondaryType}</Typography>
                                    {secondaryLabels.labels.length > 0 && (
                                        <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.75 }}>
                                            {secondaryLabels.labels.map((label) => (
                                                <Chip key={label} label={label} size="small" variant="outlined" />
                                            ))}
                                            {secondaryLabels.hiddenCount > 0 && <Chip label={`+${secondaryLabels.hiddenCount}`} size="small" variant="outlined" />}
                                        </Box>
                                    )}
                                </Box>
                            )}
                        </Box>
                    </Box>
                )}
            </CardContent>
            {!readOnly && (
                <CardActions sx={{ justifyContent: 'space-between', px: 2, pb: 2, pt: item ? 0 : 2 }}>
                    <Typography color="text.secondary" variant="caption">{item ? `Day ${day}` : 'Empty slot'}</Typography>
                    {!item && onAddDay && (
                        <Button onClick={() => onAddDay(day)} size="small" startIcon={<AddRoundedIcon />} variant="contained">
                            Add Day
                        </Button>
                    )}
                    {item && onEditDay && (
                        <IconButton color="primary" onClick={() => onEditDay(day)}>
                            <EditOutlinedIcon />
                        </IconButton>
                    )}
                    {item && onDeleteDay && (
                        <IconButton color="error" onClick={() => onDeleteDay(day)}>
                            <DeleteOutlineRoundedIcon />
                        </IconButton>
                    )}
                </CardActions>
            )}
        </Card>
    );
};

export default ProgramDayCard;